import React from 'react';
import { ChevronDown, Inbox } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// Selector de buzones de IAmail: uno, algunos o todos. La selección es una
// lista de ids de cuenta; la lista vacía significa "Todos los buzones".

export const nombreBuzon = (cuenta) => {
  if (!cuenta) return '';
  return cuenta.nombre_cuenta || String(cuenta.email || '').split('@')[0];
};

// Etiqueta pequeña con el buzón del correo; si el mensaje está en varios
// buzones: "en 2 buzones: eventos, a.palomares".
export const EtiquetaBuzones = ({ ids, cuentasPorId, clase = 'text-[10px]' }) => {
  const lista = (ids || []).filter((id) => cuentasPorId[id]);
  if (!lista.length) return null;
  const nombres = lista.map((id) => nombreBuzon(cuentasPorId[id]));
  const texto = lista.length > 1 ? `en ${lista.length} buzones: ${nombres.join(', ')}` : nombres[0];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${clase} bg-sky-50 text-sky-700 border border-sky-100`}
      title={lista.map((id) => cuentasPorId[id].email).join(', ')}
    >
      <Inbox className="w-3 h-3" /> {texto}
    </span>
  );
};

// Normaliza una selección contra las cuentas accesibles: quita las que ya no
// existen y, si quedan todas o ninguna, devuelve [] ("Todos").
export const normalizarSeleccionBuzones = (ids, cuentas) => {
  const accesibles = (cuentas || []).map((c) => c.id);
  const validos = accesibles.filter((id) => (ids || []).includes(id));
  if (!validos.length || validos.length === accesibles.length) return [];
  return validos;
};

// "a,b" <-> ['a', 'b'] para guardar la selección en localStorage.
export const leerSeleccionBuzones = (valor) => {
  if (!valor || valor === 'todos') return [];
  return String(valor).split(',').map((x) => x.trim()).filter(Boolean);
};
export const serializarSeleccionBuzones = (ids) => (ids && ids.length ? ids.join(',') : 'todos');

// Texto del botón: "Todos los buzones", el nombre, los nombres si caben o "N buzones".
export const textoSeleccionBuzones = (seleccion, cuentas, maxCaracteres = 24) => {
  const porId = Object.fromEntries((cuentas || []).map((c) => [c.id, c]));
  const elegidos = (seleccion || []).filter((id) => porId[id]);
  if (!elegidos.length || elegidos.length === (cuentas || []).length) return 'Todos los buzones';
  const nombres = elegidos.map((id) => nombreBuzon(porId[id]));
  const juntos = nombres.join(', ');
  if (elegidos.length === 1 || juntos.length <= maxCaracteres) return juntos;
  return `${elegidos.length} buzones`;
};

const SelectorBuzones = ({
  cuentas,
  seleccion,
  onChange,
  variante = 'lateral', // 'lateral' | 'barra' | 'compacto'
  mostrarEmail = true,
  maxCaracteres,
  testid = 'selector-buzon',
}) => {
  const lista = cuentas || [];
  const elegidos = normalizarSeleccionBuzones(seleccion, lista);
  const todos = elegidos.length === 0;
  const marcado = (id) => todos || elegidos.includes(id);
  const limite = maxCaracteres || (variante === 'lateral' ? 24 : 18);
  const texto = textoSeleccionBuzones(elegidos, lista, limite);

  const alternar = (id) => {
    const actuales = todos ? lista.map((c) => c.id) : elegidos;
    const siguientes = actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id];
    // Sin ninguno marcado no se ve nada: se vuelve a "Todos".
    onChange(normalizarSeleccionBuzones(siguientes, lista));
  };

  let claseBoton;
  if (variante === 'barra') {
    claseBoton = 'shrink-0 max-w-[11rem] inline-flex items-center gap-1 text-xs border rounded-full px-2.5 py-1.5 bg-white';
  } else if (variante === 'compacto') {
    claseBoton = 'inline-flex max-w-[14rem] items-center gap-1 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-700 hover:bg-gray-50';
  } else {
    claseBoton = 'mt-1 w-full inline-flex items-center justify-between gap-1 text-sm border rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={claseBoton} title={texto} data-testid={testid}>
          <span className="inline-flex items-center gap-1 min-w-0">
            {variante !== 'lateral' && <Inbox className="w-3.5 h-3.5 shrink-0" />}
            <span className="truncate">{texto}</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2 bg-white" data-testid={`${testid}-opciones`}>
        <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm font-semibold text-gray-800">
          <input
            type="checkbox"
            className="accent-duetto-primary"
            checked={todos}
            onChange={() => onChange([])}
            data-testid={`${testid}-todos`}
          />
          Todos los buzones
        </label>
        <div className="my-1 border-t" />
        {lista.map((c) => (
          <label
            key={c.id}
            className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm text-gray-800"
          >
            <input
              type="checkbox"
              className="mt-0.5 accent-duetto-primary"
              checked={marcado(c.id)}
              onChange={() => alternar(c.id)}
              data-testid={`${testid}-${c.id}`}
            />
            <span className="min-w-0">
              <span className="block truncate">{nombreBuzon(c)}</span>
              {mostrarEmail && c.email && <span className="block truncate text-[11px] text-gray-500">{c.email}</span>}
            </span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default SelectorBuzones;
